import { MutableRefObject, useRef } from 'react';
import { use_effect } from './use_effect';

export function use_ref_with_fallback<T>(value_default: T): MutableRefObject<T> {
  const ref = useRef<T>(value_default);

  /*
   * EFFECTS
   */

  // Ensure ref always has a value by restoring the stub if null
  use_effect(() => {
    if (ref.current === null) ref.current = value_default;

    // Create a proxy to intercept when ref.current is set to null
    let ref_current_original = ref.current;

    // Store the original descriptor
    const descriptor = Object.getOwnPropertyDescriptor(ref, 'current');

    if (descriptor && descriptor.configurable) {
      Object.defineProperty(ref, 'current', {
        configurable: true,
        enumerable: true,
        get: function () {
          return ref_current_original;
        },
        set: function (value) {
          // If trying to set to null/undefined, use stub instead
          if (value === null || value === undefined) {
            value = value_default;
          }

          // Otherwise, update normally
          ref_current_original = value;
        },
      });
    }

    return () => {
      // Clean up - restore original descriptor if possible
      if (!descriptor) return;
      if (!descriptor.configurable) return;

      Object.defineProperty(ref, 'current', descriptor);
    };
  }, [value_default]);

  return ref;
}
